import React, { useEffect } from 'react';
import { CheckCircle, Star } from '@mui/icons-material';

const PaymentSuccessAnimation = ({ show, onComplete, amount }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onComplete?.();
      }, 3000); // Hide after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl max-w-sm w-full mx-4 text-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20"></div>
        
        {/* Animated entrance */}
        <div className="relative z-10 animate-in slide-in-from-bottom-8 duration-700">
          {/* Success icon with rings */}
          <div className="relative mb-6 flex justify-center">
            {/* Outer ring - slow pulse */}
            <div className="absolute w-32 h-32 border-4 border-green-200 dark:border-green-700 rounded-full animate-ping opacity-20"></div>
            {/* Middle ring - medium pulse */}
            <div className="absolute w-24 h-24 border-4 border-green-300 dark:border-green-600 rounded-full animate-pulse opacity-40"></div>
            {/* Inner ring - fast pulse */}
            <div className="absolute w-16 h-16 border-2 border-green-400 dark:border-green-500 rounded-full animate-ping opacity-60"></div>
            
            {/* Main success icon */}
            <div className="relative w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
              <CheckCircle className="w-10 h-10 text-white animate-in zoom-in duration-500 delay-300" />
            </div>
          </div>
          
          {/* Success message */}
          <div className="space-y-4">
            <div className="animate-in slide-in-from-bottom-4 duration-500 delay-500">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Payment Successful!
              </h2>
              <div className="flex items-center justify-center space-x-2 text-green-600 dark:text-green-400">
                <Star className="w-5 h-5 animate-spin" />
                <span className="text-lg font-semibold">
                  Rs. {amount || '0'}
                </span>
                <Star className="w-5 h-5 animate-spin" style={{ animationDirection: 'reverse' }} />
              </div>
            </div>
            
            <div className="animate-in slide-in-from-bottom-4 duration-500 delay-700">
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Your payment has been processed successfully
              </p>
            </div>
          </div>
          
          {/* Floating particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full opacity-0 animate-pulse"
                style={{
                  left: `${10 + (i * 10)}%`,
                  top: `${20 + (i % 3) * 20}%`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: '2s',
                  animationIterationCount: 'infinite'
                }}
              />
            ))}
          </div>
          
          {/* Progress bar */}
          <div className="mt-6 animate-in slide-in-from-bottom-4 duration-500 delay-1000">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
              <div 
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-1 rounded-full animate-in slide-in-from-left duration-3000"
                style={{ width: '100%' }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Closing automatically...
            </p>
          </div>
        </div>
        
        {/* Confetti effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              className="absolute w-3 h-3 opacity-0"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-10px`,
                backgroundColor: ['#10b981', '#059669', '#34d399', '#6ee7b7'][i % 4],
                borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                animation: `confetti-fall 2s ease-out ${i * 0.1}s 1 forwards`
              }}
            />
          ))}
        </div>
      </div>
      
      <style jsx>{`
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(-10px) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(400px) rotate(360deg);
          }
        }
        
        .animate-in {
          animation-fill-mode: both;
        }
        
        .slide-in-from-bottom-8 {
          animation: slide-in-from-bottom-8 0.7s ease-out;
        }
        
        .slide-in-from-bottom-4 {
          animation: slide-in-from-bottom-4 0.5s ease-out;
        }
        
        .slide-in-from-left {
          animation: slide-in-from-left 3s ease-out;
        }
        
        .zoom-in {
          animation: zoom-in 0.5s ease-out;
        }
        
        .delay-300 { animation-delay: 0.3s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-700 { animation-delay: 0.7s; }
        .delay-1000 { animation-delay: 1s; }
        
        .duration-500 { animation-duration: 0.5s; }
        .duration-700 { animation-duration: 0.7s; }
        .duration-3000 { animation-duration: 3s; }
        
        @keyframes slide-in-from-bottom-8 {
          0% {
            transform: translateY(32px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slide-in-from-bottom-4 {
          0% {
            transform: translateY(16px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slide-in-from-left {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(0);
          }
        }
        
        @keyframes zoom-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccessAnimation;